// Pure shareId router for the share-web SPA.
//
// Mirrors the worker-side validator (`apps/share-worker/functions/api/shares/[shareId].ts`):
// shareId must be 8–64 chars of [a-zA-Z0-9-]. The SPA reads the id straight
// from `location.pathname`, so the router has to reject anything that is not
// exactly a single matching segment (optionally trailing `/`).

const SHARE_PATH_RE = /^\/([a-zA-Z0-9-]{8,64})\/?$/;

/**
 * Extracts a shareId from a pathname.
 *
 * Returns the capture group if `pathname` matches `^/<id>/?$` with id length
 * 8–64 from `[a-zA-Z0-9-]`. Returns `null` for root (`/`), empty, multi-segment
 * paths, or ids shorter than 8 characters.
 */
export function parseShareIdFromPath(pathname: string): string | null {
  const match = SHARE_PATH_RE.exec(pathname);
  return match ? match[1]! : null;
}

/** True when the path is the team-library view (`/library` or `/library/`). */
export function isLibraryPath(pathname: string): boolean {
  return pathname === '/library' || pathname === '/library/';
}

const SETLIST_PATH_RE = /^\/setlist\/([a-zA-Z0-9-]{1,64})\/?$/;

/** Returns `{ setlistId }` for `/setlist/<id>` (optional trailing slash), else null. */
export function isSetlistPath(pathname: string): { setlistId: string } | null {
  const m = SETLIST_PATH_RE.exec(pathname);
  return m ? { setlistId: m[1]! } : null;
}

// Экран сетлиста из ссылки слов (единый сетлист, фаза 1): `/setlist/w/<uuid>`.
// Двухсегментный хвост под SETLIST_PATH_RE не подпадает (группа захвата
// `[a-zA-Z0-9-]` не пускает `/`), поэтому одна ссылка не может значить и
// сетлист со звуком, и ссылку слов. Форма id — как у `/t/` (см.
// TEXT_SHARE_PATH_RE ниже): ссылки минтит `crypto.randomUUID()` на воркере, и
// всё остальное воркер встречает 404.
const WORDS_SETLIST_PATH_RE = /^\/setlist\/w\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\/?$/i;

/** Возвращает id для `/setlist/w/<uuid>` (можно с конечным слешем), иначе null. */
export function parseWordsSetlistId(pathname: string): string | null {
  const m = WORDS_SETLIST_PATH_RE.exec(pathname);
  return m ? m[1]! : null;
}

// Гостевой экран единого сетлиста (фаза 3): `/s/<uuid>[?g=<guestKey>]`.
// setlistId — UUID (сервер минтит их так же, как shareId/setlistId других
// сущностей), форма id — как у WORDS_SETLIST_PATH_RE/TEXT_SHARE_PATH_RE выше.
// Ключ гостя читается из query (`g`), не из пути: путь остаётся один и тот же
// для члена команды (кука) и для анонимного гостя (ключ), а ссылка без `g`
// просто не даёт анониму доступа — сервер решает это по `?g=`.
const GUEST_SETLIST_PATH_RE = /^\/s\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\/?$/i;

/**
 * Возвращает `{ setlistId, guestKey }` для `/s/<uuid>` (можно с конечным
 * слешем); `guestKey` — значение query-параметра `g`, или `null`, если его
 * нет или он пуст. Для не-UUID id или лишних сегментов — `null`.
 */
export function parseGuestSetlist(pathname: string, search: string): { setlistId: string; guestKey: string | null } | null {
  const m = GUEST_SETLIST_PATH_RE.exec(pathname);
  if (!m) return null;
  const setlistId = m[1]!;
  const g = new URLSearchParams(search).get('g');
  return { setlistId, guestKey: g ? g : null };
}

/** True when the path is the admin view (`/admin` or `/admin/`). */
export function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname === '/admin/';
}

/** True when the path is the login view (`/login` or `/login/`). */
export function isLoginPath(pathname: string): boolean {
  return pathname === '/login' || pathname === '/login/';
}

/** True when the path is the team-dashboard view (`/dashboard` or `/dashboard/`). */
export function isDashboardPath(pathname: string): boolean {
  return pathname === '/dashboard' || pathname === '/dashboard/';
}

/** True when the path is the ops dashboard (`/ops` or `/ops/`). */
export function isOpsPath(pathname: string): boolean {
  return pathname === '/ops' || pathname === '/ops/';
}

const INVITE_PATH_RE = /^\/invite\/([a-zA-Z0-9_-]{1,128})\/?$/;

/** Returns the invite token for `/invite/<token>` (optional trailing slash), else null. */
export function parseInviteToken(pathname: string): string | null {
  const m = INVITE_PATH_RE.exec(pathname);
  return m ? m[1]! : null;
}

// Текстовая ссылка на сетлист (волна B): `/t/<uuid>` — слова и аккорды, без
// аудио и без логина.
//
// Префикс `/t/` — ТРЕБОВАНИЕ, а не сокращение ради красоты. Голый UUID это 36
// символов из `[0-9a-f-]`, то есть он целиком подходит под `SHARE_PATH_RE`
// (8–64 из `[a-zA-Z0-9-]`), и `/<uuid>` был бы разобран как shareId
// АУДИО-шейра — страница пошла бы за манифестом и показала читателю ошибку
// загрузки вместо слов. Двухсегментный путь под то правило не попадает вовсе,
// поэтому одна и та же ссылка не может значить две разные вещи. Заперто
// тестом «/t/<uuid> НЕ проваливается в разбор аудио-шейра».
//
// Форма id мирроит воркер (`isUuid`, `apps/share-worker/functions/_lib/
// validate.ts`), а не общий charset соседей: ссылки минтит
// `crypto.randomUUID()`, и всё остальное воркер встречает 404. Разобрать здесь
// «/t/abcd1234» значило бы сходить за ним в сеть, чтобы услышать то же самое.
const TEXT_SHARE_PATH_RE = /^\/t\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\/?$/i;

/** Возвращает id для `/t/<uuid>` (можно с конечным слешем), иначе null. */
export function parseTextShareId(pathname: string): string | null {
  const m = TEXT_SHARE_PATH_RE.exec(pathname);
  return m ? m[1]! : null;
}

// Вход на страницу слов с кнопки «Live · song N» экрана сетлиста (B-261002).
//
// Голый `/t/<uuid>` — это ссылка, разосланная в чат, и main.ts уводит её на
// экран сетлиста `/s/<id>`, если ссылка в нём сидит (задача D1). А сидит она
// там всегда: гостевой ключ сетлиста — это id его ссылки слов. Поэтому кнопка
// Live, ведущая на голый `/t/`, возвращала читателя туда же, откуда он пришёл,
// и страница слов с живой подсветкой была недостижима. Метка `?live=1` говорит
// «читатель уже на экране сетлиста и просит слова», и редирект её пропускает.
const LIVE_WORDS_PARAM = 'live';

/** Адрес страницы слов для кнопки Live (`/t/<id>?live=1`). */
export function liveWordsPath(textShareId: string): string {
  return `/t/${textShareId}?${LIVE_WORDS_PARAM}=1`;
}

/** true, когда страницу слов открыли с кнопки Live, а не по ссылке из чата. */
export function isLiveWordsEntry(search: string): boolean {
  return new URLSearchParams(search).has(LIVE_WORDS_PARAM);
}

// Страница слов одной песни (волна C): `/words/<id>`.
//
// Идентификатор публикации слов — UUID из 36 символов, то есть он целиком
// подходит под SHARE_PATH_RE (8–64 из [a-zA-Z0-9-]) и без префикса был бы
// разобран как shareId АУДИО-шейра. Двухсегментный путь под то правило не
// попадает вовсе, поэтому одна и та же ссылка не может значить две разные
// вещи. Заперто тестом «/words/<uuid> НЕ проваливается в разбор аудио-шейра».
const SONG_WORDS_PATH_RE = /^\/words\/([a-zA-Z0-9-]{1,64})\/?$/;

/** Возвращает id публикации слов для `/words/<id>`, иначе null. */
export function parseSongWordsId(pathname: string): string | null {
  const m = SONG_WORDS_PATH_RE.exec(pathname);
  return m ? m[1]! : null;
}
